import { useEffect, useRef, type RefObject } from "react";
import { useLocation } from "react-router-dom";
import { useStore } from "react-redux";
import type { RootState } from "state/store";
import { useAppSelector } from "./hookStore";

const NEVER_REFRESHED_WAIT_MS = 120;
const RESIZE_RESTORE_BUDGET_MS = 1500;

/**
 * Captures `containerRef.scrollTop` when the global iframe modal opens and restores
 * it after the modal closes and post-close RTK invalidation / refetch settles
 * (`isIframeRefreshing` false). Uses rAF + ResizeObserver on the inner content column
 * (when provided) to recover from layout shifts that would otherwise clamp scrollTop.
 */
export function useIframeModalScrollRestoration(
  containerRef: RefObject<HTMLElement | null>,
  scrollInnerRef?: RefObject<HTMLElement | null>
) {
  const store = useStore<RootState>();
  const location = useLocation();

  const iframeModal = useAppSelector((s) => s.layout.iframeModal);
  const isIframeRefreshing = useAppSelector((s) => s.layout.isIframeRefreshing);

  const savedTopRef = useRef<number | null>(null);
  const prevOpenRef = useRef(false);
  const locationKeyRef = useRef(location.key);
  /** Persists across effect re-runs while `isIframeRefreshing` toggles (Bug 10). */
  const sawRefreshingRef = useRef(false);

  // Cancel a pending restore if the user navigates while the modal flow is in flight.
  useEffect(() => {
    const prev = locationKeyRef.current;
    if (prev !== location.key) {
      locationKeyRef.current = location.key;
      if (savedTopRef.current !== null) {
        savedTopRef.current = null;
      }
    }
  }, [location.key]);

  useEffect(() => {
    if (isIframeRefreshing) {
      sawRefreshingRef.current = true;
    }
  }, [isIframeRefreshing]);

  // Capture scroll position when the iframe modal transitions closed -> open.
  useEffect(() => {
    const open = iframeModal.isOpen;
    if (open && !prevOpenRef.current) {
      sawRefreshingRef.current = false;
      const el = containerRef.current;
      if (el) {
        savedTopRef.current = el.scrollTop;
      }
    }
    prevOpenRef.current = open;
  }, [iframeModal.isOpen, containerRef]);

  // Restore after modal closes and refresh overlay has finished (or refresh was skipped).
  useEffect(() => {
    if (iframeModal.isOpen || savedTopRef.current === null) {
      return undefined;
    }

    let cancelled = false;
    let rafId = 0;
    const cleanupFns: Array<() => void> = [];
    const startTime = performance.now();

    const applySaved = () => {
      const el = containerRef.current;
      const saved = savedTopRef.current;
      if (!el || saved == null) {
        return;
      }
      const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
      el.scrollTop = Math.min(saved, maxScroll);
    };

    const finish = () => {
      savedTopRef.current = null;
      cleanupFns.forEach((fn) => fn());
      cleanupFns.length = 0;
    };

    const schedule = () => {
      if (cancelled) {
        return;
      }

      const { isIframeRefreshing: refreshing, iframeModal: modal } =
        store.getState().layout;

      if (modal.isOpen) {
        return;
      }

      if (refreshing) {
        sawRefreshingRef.current = true;
      }

      const elapsed = performance.now() - startTime;
      const ready =
        !refreshing &&
        (sawRefreshingRef.current || elapsed > NEVER_REFRESHED_WAIT_MS);

      if (!ready) {
        rafId = requestAnimationFrame(schedule);
        return;
      }

      // After refresh: one rAF so layout from invalidations has committed, then apply + observe.
      rafId = requestAnimationFrame(() => {
        if (cancelled) {
          return;
        }

        applySaved();

        const observeTarget =
          scrollInnerRef?.current ?? containerRef.current;
        if (!observeTarget) {
          finish();
          return;
        }

        const ro = new ResizeObserver(() => {
          applySaved();
        });
        ro.observe(observeTarget);
        cleanupFns.push(() => ro.disconnect());

        const deadline = performance.now() + RESIZE_RESTORE_BUDGET_MS;
        const loop = () => {
          if (cancelled) {
            return;
          }
          applySaved();
          if (performance.now() < deadline) {
            rafId = requestAnimationFrame(loop);
          } else {
            finish();
          }
        };
        rafId = requestAnimationFrame(loop);
      });
    };

    rafId = requestAnimationFrame(schedule);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      cleanupFns.forEach((fn) => fn());
    };
  }, [
    iframeModal.isOpen,
    isIframeRefreshing,
    location.key,
    store,
    containerRef,
    scrollInnerRef,
  ]);
}
